import {
    Box,
    Button,
    Grid,
    Card,
    CardContent,
    Typography,
    IconButton,
    Chip,
    Stack,
    CircularProgress,
} from '@mui/material';
import Iconify from 'src/components/iconify';
import { useState } from 'react';
import { useMutation } from '@apollo/client';
import { DELETE_PACKAGE } from 'src/graphql/mutations/packages';
import { useSnackbar } from 'notistack';
import PackageDialog from './PackageDialog';

export default function PackageManagement({ packages, products, loading, refetch }) {
    const [openDialog, setOpenDialog] = useState(false);
    const [editingPackage, setEditingPackage] = useState(null);
    const { enqueueSnackbar } = useSnackbar();
    
    const [deletePackage, { loading: deleteLoading }] = useMutation(DELETE_PACKAGE, {
        onCompleted: () => {
            enqueueSnackbar('Package deleted successfully!', { variant: 'success' });
            refetch();
        },
        onError: (error) => {
            enqueueSnackbar(error.message || 'Failed to delete package', { variant: 'error' });
        },
    });

    const handleOpenDialog = (pkg = null) => {
        setEditingPackage(pkg);
        setOpenDialog(true);
    };

    const handleCloseDialog = () => {
        setEditingPackage(null);
        setOpenDialog(false);
    };

    const handleDelete = (packageId) => {
        if (window.confirm('Are you sure you want to delete this package?')) {
            deletePackage({ variables: { packageId } });
        }
    };

    const getPackageIcon = (packageType) => {
        const icons = {
            'BRONZE BASIC': 'eva:star-outline',
            'SILVER STANDARD': 'eva:award-outline',
            'GOLD PREMIUM': 'eva:trophy-outline',
            'PLATINUM DELUXE': 'eva:flash-outline',
        };
        return icons[packageType] || 'eva:cube-outline';
    };

    const getPackageColor = (packageType) => {
        const colors = {
            'BRONZE BASIC': '#CD7F32',
            'SILVER STANDARD': '#C0C0C0',
            'GOLD PREMIUM': '#FFD700',
            'PLATINUM DELUXE': '#E5E4E2',
        };
        return colors[packageType] || '#000';
    };

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
                <CircularProgress />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3 }}>
            <Stack direction="row" justifyContent="flex-end" mb={3}>
                <Button
                    variant="contained"
                    startIcon={<Iconify icon="eva:plus-fill" />}
                    onClick={() => handleOpenDialog()}
                >
                    Create New Package
                </Button>
            </Stack>

            <Grid container spacing={3}>
                {packages?.map((pkg) => (
                    <Grid item xs={12} md={6} lg={4} key={pkg._id}>
                        <Card
                            sx={{
                                position: 'relative',
                                '&:hover': {
                                    boxShadow: 6,
                                },
                            }}
                        >
                            <Box
                                sx={{
                                    position: 'absolute',
                                    top: 12,
                                    right: 12,
                                    display: 'flex',
                                    gap: 1,
                                }}
                            >
                                <IconButton
                                    size="small"
                                    onClick={() => handleOpenDialog(pkg)}
                                    sx={{ bgcolor: 'background.paper' }}
                                >
                                    <Iconify icon="eva:edit-2-fill" />
                                </IconButton>
                                <IconButton
                                    size="small"
                                    onClick={() => handleDelete(pkg._id)}
                                    disabled={deleteLoading}
                                    sx={{ bgcolor: 'background.paper' }}
                                >
                                    <Iconify icon="eva:trash-2-fill" />
                                </IconButton>
                            </Box>

                            <CardContent>
                                <Box display="flex" alignItems="center" mb={2}>
                                    <Box
                                        sx={{
                                            width: 48,
                                            height: 48,
                                            borderRadius: 1,
                                            bgcolor: getPackageColor(pkg.packageType),
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            mr: 2,
                                        }}
                                    >
                                        <Iconify
                                            icon={getPackageIcon(pkg.packageType)}
                                            sx={{ color: 'white', width: 28, height: 28 }}
                                        />
                                    </Box>
                                    <Box flex={1}>
                                        <Typography variant="h6" noWrap>
                                            {pkg.name || pkg.packageType}
                                        </Typography>
                                        <Chip
                                            label={pkg.packageType}
                                            size="small"
                                            sx={{
                                                bgcolor: getPackageColor(pkg.packageType),
                                                color: 'white',
                                                fontWeight: 'bold',
                                            }}
                                        />
                                    </Box>
                                </Box>

                                <Typography variant="h4" color="primary" mb={2}>
                                    ₦{pkg.amount?.toLocaleString()}
                                </Typography>

                                <Typography variant="subtitle2" color="text.secondary" mb={1}>
                                    Included Products ({pkg.products?.length || 0}):
                                </Typography>
                                <Box>
                                    {pkg.products?.length > 0 ? (
                                        pkg.products.map((product) => (
                                            <Chip
                                                key={product._id}
                                                label={product.productName}
                                                size="small"
                                                sx={{ m: 0.5 }}
                                                variant="outlined"
                                            />
                                        ))
                                    ) : (
                                        <Typography variant="body2" color="text.secondary" fontStyle="italic">
                                            No products added
                                        </Typography>
                                    )}
                                </Box>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}

                {packages?.length === 0 && (
                    <Grid item xs={12}>
                        <Box textAlign="center" py={5}>
                            <Iconify icon="eva:inbox-outline" sx={{ fontSize: 80, color: 'text.disabled', mb: 2 }} />
                            <Typography variant="h6" color="text.secondary">
                                No packages found. Create your first package!
                            </Typography>
                        </Box>
                    </Grid>
                )}
            </Grid>

            <PackageDialog
                open={openDialog}
                onClose={handleCloseDialog}
                package={editingPackage}
                products={products}
                refetch={refetch}
            />
        </Box>
    );
}
